import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import type { UplistingConnectionStatus } from '@/api/client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { formatLocaleDateTime } from '@/lib/format-utils'

/**
 * The Uplisting v3 OAuth connection, with a re-consent link that cannot silently
 * cost the connection a permission.
 *
 * A re-consent REPLACES the stored token and only asks Uplisting for
 * `requested_scopes`, so any granted scope outside that list is dropped. The live
 * token carries calendar:write that the requested list lacks, which is the
 * mistake this card exists to stop. admin-api computes `would_drop`; the card
 * never derives it, and never offers dropping a scope as the fix.
 *
 * When the diff cannot be known (status failed to load, or the API predates
 * `would_drop`, or the token has no recorded scope) the button stays behind the
 * same acknowledgement rather than reading "nothing to drop".
 */

type ConnectionState = 'not_connected' | 'expired' | 'connected'

function connectionState(status: UplistingConnectionStatus, now: number): ConnectionState {
  if (!status.connected) return 'not_connected'
  const expiresAt = status.expires_at ? Date.parse(status.expires_at) : NaN
  // An expired access token with a refresh token renews itself on the next use,
  // so only a token that cannot renew counts as expired.
  if (Number.isFinite(expiresAt) && expiresAt <= now && !status.has_refresh_token) return 'expired'
  return 'connected'
}

function ScopeChips({ scopes, atRisk = [] }: { scopes: string[]; atRisk?: string[] }) {
  if (scopes.length === 0) return <span className="text-sm text-textFaint">None</span>
  return (
    <ul className="flex flex-wrap gap-1.5">
      {scopes.map(scope => {
        const risky = atRisk.includes(scope)
        return (
          <li
            key={scope}
            className={
              risky
                ? 'rounded-lg border border-amber/40 bg-warningBg px-2 py-0.5 font-mono text-xs text-amber'
                : 'rounded-lg border border-border bg-surface2 px-2 py-0.5 font-mono text-xs text-text'
            }
          >
            {scope}
            {risky && <span className="font-sans"> - not requested</span>}
          </li>
        )
      })}
    </ul>
  )
}

export function UplistingConnectionCard() {
  // The exact at-risk list the operator ticked for, so a refreshed status that
  // changes what would be dropped cannot inherit an earlier acknowledgement.
  const [acknowledgedFor, setAcknowledgedFor] = useState<string | null>(null)
  // Read once per mount: a timestamp comparison in render must not call Date.now() repeatedly.
  const [now] = useState(() => Date.now())

  const {
    data: status,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ['uplisting-connection-status'],
    queryFn: ({ signal }) => api.getUplistingConnectionStatus(signal),
    // Returning to Settings must not show a cached "nothing would be dropped".
    refetchOnMount: 'always',
  })

  const reconsent = () => {
    window.location.assign(api.getUplistingOAuthAuthorizeUrl())
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Uplisting connection</CardTitle>
        </CardHeader>
        <CardContent>
          <LoadingSkeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    )
  }

  if (error || !status) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Uplisting connection</CardTitle>
          <CardDescription>
            The connection status could not be loaded, so Capehost cannot tell what a re-consent
            would drop. Re-consent is unavailable until it loads.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p role="alert" className="text-sm text-danger">
            {error instanceof Error ? error.message : 'Unknown error'}
          </p>
          <Button variant="outline" onClick={() => refetch()}>
            Retry
          </Button>
        </CardContent>
      </Card>
    )
  }

  const state = connectionState(status, now)
  const requested = status.requested_scopes
  const granted = status.scope ? status.scope.split(/\s+/).filter(Boolean) : null
  const wouldDrop = status.would_drop
  const diffKnown = requested !== undefined && wouldDrop !== undefined
  const scopesUnknown = status.connected && granted === null
  const atRisk = wouldDrop ?? []
  // Nothing to acknowledge when not connected: there is no token to lose a scope from.
  const needsAck = status.connected && (!diffKnown || scopesUnknown || atRisk.length > 0)
  const acknowledged = acknowledgedFor === atRisk.join(',')
  // Not while a refresh is in flight: the cached status may be stale, and the
  // diff it shows is exactly what this button must not act on blind.
  const canReconsent =
    !isFetching && (!status.connected || (diffKnown && (!needsAck || acknowledged)))

  const badge =
    state === 'not_connected' ? (
      <Badge variant="destructive">Not connected</Badge>
    ) : state === 'expired' ? (
      <Badge variant="destructive">Token expired</Badge>
    ) : (
      <Badge variant="success">Connected</Badge>
    )

  const description =
    state === 'not_connected'
      ? 'Capehost has no Uplisting v3 token, so v3 features (calendar, messages, reviews) return an error until someone connects it.'
      : state === 'expired'
        ? 'v3 features are failing until the token is renewed.'
        : 'The token renews itself. Re-consent only when a new permission is needed.'

  const buttonLabel =
    state === 'not_connected' ? 'Connect Uplisting' : needsAck ? 'Re-consent anyway' : 'Re-consent'

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2.5">
          Uplisting connection {badge}
          {atRisk.length > 0 && (
            <Badge variant="warning">
              {atRisk.length} {atRisk.length === 1 ? 'scope' : 'scopes'} at risk
            </Badge>
          )}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {status.connected && (
          <dl className="grid gap-4 sm:grid-cols-3">
            <div>
              <dt className="text-xs text-textFaint">
                {state === 'expired' ? 'Expired at' : 'Token expires'}
              </dt>
              <dd className="text-sm tabular-nums">{formatLocaleDateTime(status.expires_at)}</dd>
            </div>
            <div>
              <dt className="text-xs text-textFaint">Auto-renew</dt>
              <dd className="text-sm">
                {status.has_refresh_token ? 'On (refresh token held)' : 'Off (no refresh token)'}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-textFaint">Last updated</dt>
              <dd className="text-sm tabular-nums">{formatLocaleDateTime(status.updated_at)}</dd>
            </div>
          </dl>
        )}

        {state === 'expired' && (
          <div role="alert" className="rounded-xl border border-danger/35 bg-danger/10 p-4 text-sm">
            <p className="font-semibold text-danger">
              The token has expired and has no refresh token to renew it.
            </p>
            <p className="mt-1 text-textMuted">
              Re-consent is the only way to reconnect. The new token will only carry the requested
              scopes, so the same drop guard applies as for a live token.
            </p>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          {status.connected && (
            <div>
              <h4 className="mb-2 text-xs font-semibold text-textMuted">
                Granted <span className="font-normal text-textFaint">(what the token has now)</span>
              </h4>
              {granted ? (
                <ScopeChips scopes={granted} atRisk={atRisk} />
              ) : (
                <p className="text-sm text-textFaint">Not recorded for this token</p>
              )}
            </div>
          )}
          <div>
            <h4 className="mb-2 text-xs font-semibold text-textMuted">
              {status.connected ? 'Requested on re-consent' : 'A new connection will request'}{' '}
              <span className="font-normal text-textFaint">(what Uplisting will ask for)</span>
            </h4>
            {requested ? (
              <ScopeChips scopes={requested} />
            ) : (
              <p className="text-sm text-textFaint">Unknown</p>
            )}
          </div>
        </div>

        {needsAck && (
          <div
            role="alert"
            className="space-y-3 rounded-xl border border-amber/35 bg-warningBg p-4"
          >
            {atRisk.length > 0 ? (
              <p className="font-semibold text-amber">
                Re-consenting will drop: {atRisk.join(', ')}
              </p>
            ) : (
              <p className="font-semibold text-amber">
                Capehost cannot tell what a re-consent would drop.
              </p>
            )}
            <p className="text-sm">
              A re-consent replaces the token, and it only asks for the scopes listed as requested.
              Anything the token has beyond that is lost, and cannot be added back without another
              consent.
            </p>
            <p className="text-sm text-textMuted">
              If you need to keep {atRisk.length > 0 ? atRisk.join(', ') : 'these scopes'}, do not
              re-consent here. Ask engineering to add {atRisk.length > 0 ? 'it' : 'them'} to the
              requested list first.
            </p>
            {diffKnown && (
              <label className="flex cursor-pointer items-start gap-2.5 text-sm">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 accent-mint"
                  checked={acknowledged}
                  onChange={e => setAcknowledgedFor(e.target.checked ? atRisk.join(',') : null)}
                />
                <span>
                  {atRisk.length > 0
                    ? `I understand ${atRisk.join(', ')} will be removed from the connection.`
                    : 'I understand any scope not requested will be removed from the connection.'}
                </span>
              </label>
            )}
          </div>
        )}

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="max-w-prose text-xs text-textFaint">
            {state === 'not_connected'
              ? "Opens Uplisting's consent screen. You return here when you approve."
              : needsAck && !acknowledged
                ? diffKnown
                  ? 'Tick the box above to enable the button.'
                  : 'Unavailable until the API reports what would be dropped.'
                : "Opens Uplisting's consent screen, then returns here."}
          </p>
          <Button
            variant={
              state === 'not_connected' || (state === 'expired' && !needsAck)
                ? 'default'
                : 'outline'
            }
            disabled={!canReconsent}
            onClick={reconsent}
          >
            {buttonLabel}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
